// Cycle 407 — the check-in that asks before it gets loud: the PURE half.
//
// Everything here is a function of server-provided state and the wall
// clock. Nothing decides whether the alarm sounds; nothing consults quiet
// hours; nothing knows what a glucose value MEANS (the server is the
// arbiter — an answer never outranks the number). The screen composes its
// sentences from these so the tests can pin the words without a DOM.
//
// ⚠ THE CHECK-IN SPEECH IGNORES `tts_quiet_hours` BY DESIGN. This is
// alarm-path speech inside a live urgent-low episode, not an ambient
// announcement — the same reasoning that keeps the alarm loop, the urgent
// overlay and the escalation rails outside the voice quiet window (cycle
// 330). Nothing in this module or in `TreatingScreen` imports
// `lib/quietHours`, and a source guard pins that.

import type {
  Alert,
  CustodyCheckinOutcome,
  CustodyCheckinQuestion,
} from "./types";

/** How long the ring counts down from — the ONE window (cycle 404). Cycle
 *  427: every window, every "yes", every re-tap — no cap. */
export const TREATING_RING_TOTAL_MS = 600 * 1000;

/** Cycle 427: the two new prompt kinds and the dark-expiry check-in carry
 *  a 60 s server-authored deadline (`alerts_service.CUSTODY_PROMPT_SECONDS`).
 *  Mirrored for the countdown's initial render only; the deadline on the
 *  frame is the authority. */
export const PROMPT_SECONDS = 60;

/** Silence at the first question is never yes: ONE local re-prompt, this
 *  long after the question was spoken, if it is still unanswered. Then the
 *  window simply runs to expiry, exactly as it does today. */
export const CHECKIN_REPROMPT_MS = 30 * 1000;

/** Cycle 437: how long a SUCCESSFUL check-in confirmation stays on screen.
 *  Failures are NOT governed by this — they stay until the next question or
 *  the window ends (the cycle-416 rule). Long enough to read a short line
 *  that was also spoken; short enough that it does not hold 30px of the
 *  screen's vertical stack for the rest of a ten-minute window, which is
 *  what evicted the insulin-on-board instrument on the kiosk. */
export const CHECKIN_REPLY_VISIBLE_MS = 8 * 1000;

/** How long the hand-back reason line stays on the returned alarm. */
export const HANDBACK_LINE_MS = 30 * 1000;

/** A hung check-in POST must never dead-button the screen: after this
 *  long the answer resolves to "timeout", the question is un-hidden and
 *  the control re-enables (`apiClient` carries no abort signal, so this is
 *  the ceiling). Finite by construction; a source guard pins it. */
export const CHECKIN_REQUEST_TIMEOUT_MS = 15000;

/** The spoken + shown question, per prompt KIND (cycle 427). `name` and
 *  `contactName` are rendered as React text nodes (never markup) and
 *  handed to `speak()` as plain text — there is no surface here that
 *  renders them into HTML.
 *
 *  `checkin` and `worsening` ask the same question — the worsening prompt
 *  IS the check-in pulled forward; the meta line beside it says why
 *  ("<member> dropped to N"). `still_treating` names the declared
 *  caregiver when there is one ("Is Hero 3 still treating the member?") and asks
 *  about "someone" under a quiet window or an anonymous declaration. */
export function checkinQuestionText(
  question: CustodyCheckinQuestion,
  memberName: string | null | undefined,
  contactName?: string | null,
): string {
  const who = memberName && memberName.trim() ? memberName.trim() : "them";
  switch (question) {
    case "checkin":
    case "worsening":
      return `Is everything OK with ${who}?`;
    case "still_treating": {
      const c = contactName && contactName.trim() ? contactName.trim() : null;
      return c ? `Is ${c} still treating ${who}?` : `Is someone still treating ${who}?`;
    }
  }
}

/** Cycle 427: the meta-row line under a worsening prompt — the live value
 *  the server acted on is the reading the frames already carry (the client
 *  derives it; no column). `null` when there is nothing to say. */
export function worseningMetaLine(
  memberName: string | null | undefined,
  valueText: string | null | undefined,
): string | null {
  if (!valueText) return null;
  const who = memberName && memberName.trim() ? memberName.trim() : "They";
  return `${who} dropped to ${valueText}`;
}

/** Cycle 427: the meta-row line while help is being asked for beneath an
 *  open window — `calling` while the ladder walks the other heroes,
 *  `accepted` once one pressed 1. Names come resolved from the server. */
export function helpMetaLine(
  state: "calling" | "accepted" | null | undefined,
  contactName: string | null | undefined,
): string | null {
  const c = contactName && contactName.trim() ? contactName.trim() : null;
  if (state === "calling") {
    return c ? `Backup requested — calling ${c}…` : "Backup requested — calling the other heroes…";
  }
  if (state === "accepted") {
    return c ? `${c} is on the way` : "A hero is on the way";
  }
  return null;
}

/** Cycle 427: No Data mode is a RENDERING state, derived from the reading
 *  the frames already carry — never a custody state, never a column. The
 *  number greys and its age is shown; every button stays. */
export function noDataValueLine(ageMinutes: number | null | undefined): string {
  if (ageMinutes == null || !Number.isFinite(ageMinutes)) return "No data";
  return `No data · ${Math.max(0, Math.floor(ageMinutes))} min`;
}

/** Cycle 427: milliseconds left on a prompt's server-authored deadline on
 *  THIS device's clock, or null when there is no live prompt. Never
 *  negative; a bad value is "no prompt" (fails closed, like
 *  `checkinPendingAt`). */
export function promptRemainingMs(
  nowMs: number,
  deadline: string | number | null | undefined,
): number | null {
  if (deadline == null || deadline === "") return null;
  const ms = typeof deadline === "number" ? deadline : Date.parse(deadline);
  if (!Number.isFinite(ms)) return null;
  return Math.max(0, ms - nowMs);
}

/** `M:SS` for the prompt countdown, ceiling like the ring. */
export function fmtPromptRemaining(ms: number): string {
  const s = Math.max(0, Math.ceil(ms / 1000));
  const mm = Math.floor(s / 60);
  const ss = s % 60;
  return `${mm}:${String(ss).padStart(2, "0")}`;
}

/** Cycle 427: the live prompt on a row — the server's kind and deadline,
 *  read the way every other custody field is. */
export function promptOf(alert: Pick<Alert, "custody_prompt_kind" | "custody_prompt_deadline"> | null | undefined): {
  kind: CustodyCheckinQuestion | null;
  deadline: string | null;
} {
  return {
    kind: alert?.custody_prompt_kind ?? null,
    deadline: alert?.custody_prompt_deadline ?? null,
  };
}

/** Cycle 450: the whole minutes left on the window, for the one line that
 *  says when alerts resume. Ceils, so a window with 9:01 left says "10". */
export function remainingWholeMinutes(
  remainingMs: number | null | undefined,
): number | null {
  if (remainingMs == null || !Number.isFinite(remainingMs)) return null;
  return Math.max(0, Math.ceil(remainingMs / 60_000));
}

/** The spoken reply to an answer. `null` = say nothing (a follow-up frame
 *  will speak for itself, or there is nothing to add).
 *
 *  Cycle 450: `remainingMs` is read for ONE outcome — `capped`, the
 *  operator-lever refusal — so the line can say when alerts resume. It is
 *  the window's server `custody_until` on this device's clock, never a
 *  client claim about the window. */
export function checkinReplyText(
  outcome: CustodyCheckinOutcome,
  memberName: string | null | undefined,
  remainingMs?: number | null,
): string | null {
  const who = memberName && memberName.trim() ? memberName.trim() : "them";
  switch (outcome) {
    case "extended":
      // Cycle 450: every check-in "yes" RENEWS the window to its full ten
      // minutes (the countdown on the row's next frame is the surrogate).
      return "Glad to hear it. I'll keep watching until the number comes up.";
    case "winding_down":
      // Retired in cycle 450 (a "yes" always renews now); an older server
      // may still say it. Never claims the episode is over.
      return `Glad to hear it. ${who} is coming up. I'll keep watching.`;
    case "reset":
      // Cycle 427: a worsening / still-treating "yes" — ten fresh minutes,
      // anchored to the number they just said yes about.
      return "OK. Ten more minutes. I'll keep watching.";
    case "declined":
      // Cycle 427: a still-treating "no" — the window ended and a fresh
      // search started, calls first, kiosk silent through the lead.
      return "OK. Calling the other heroes now.";
    case "backup":
      // Cycle 427: the window STAYS OPEN; the other heroes are texted and
      // rung one at a time beneath it. Nothing here is "back on".
      return "Asking the other heroes for backup now. Calling them one at a time.";
    case "backup_repeat":
      // The one help text of this episode already went out; say so rather
      // than claim a second ask that never left.
      return "The other heroes were already asked. Calling them now.";
    case "backup_unsent":
      // Gated on the SEND: the ladder walks, but no text could go out. A
      // screen must never announce an ask that did not happen.
      return "I couldn't text the other heroes. Calling them now.";
    case "resumed":
      return "Alerts are back on.";
    case "no_reading":
      // Retired in cycle 427 (a dark-feed "yes" extends now); an older
      // server may still say it.
      return "I can't see a reading right now, so alerts stay ready.";
    case "capped": {
      // Cycle 450 UN-RETIRED this for the operator lever
      // `SUGALARM_CUSTODY_CHECKIN_RENEWALS_MAX`: the answer was heard and
      // recorded, the window was NOT renewed, and alerts resume when it
      // ends. A FAILURE LINE — it persists (cycle 416; see
      // `checkinReplyIsTransient`) and says plainly when. On the shipping
      // default (unlimited) this branch never renders.
      const mins = remainingWholeMinutes(remainingMs);
      if (mins == null) return "OK. Alerts come back when this window ends.";
      if (mins <= 0) return "OK. This window can't be renewed again — alerts resume now.";
      return `OK. This window can't be renewed again — alerts resume in ${mins} min.`;
    }
    case "ask_more_time":
    case "resolved":
      return null;
  }
}

/** ⚠ Cycle 437: does this reply line have a bounded life, or must it stand?
 *
 *  CLASSIFIED BY WHAT THE OUTCOME MEANS, NEXT TO THE WORDS — never by HTTP
 *  disposition at the call site. The first version of this split lived in
 *  `TreatingScreen` and keyed on "the POST returned something", which put
 *  `backup_unsent` — a RAIL-FAILURE REPORT — on the transient side. That is
 *  the one line with no persistent surrogate AND a persistent surrogate that
 *  CONTRADICTS it: `helpMetaLine` goes on saying "Backup requested — calling
 *  <hero>…" while the truth ("no text could go out") erased itself after 8 s.
 *  A caregiver would never be told again, and would not text the other parent
 *  themselves. Found by the safety review.
 *
 *  TRANSIENT is allowed only where the state the line reports is redundantly
 *  and persistently on screen:
 *    extended / winding_down / reset  → the countdown ring itself
 *    declined                         → the window ends, the screen changes
 *    backup / backup_repeat           → `helpMetaLine` holds the ask
 *    resumed                          → the window ends, alerts audibly
 *                                       resume, and `custodyHandBackLine`
 *                                       carries it onto the returned alarm
 *
 *  ⚠ Do NOT justify a transient line by "it is also spoken". This component
 *  declares no speech path load-bearing, and the C378 voice failure is silent
 *  (400, not 401). The on-screen surrogate is what carries it. */
export function checkinReplyIsTransient(outcome: CustodyCheckinOutcome): boolean {
  switch (outcome) {
    case "extended":
    case "winding_down":
    case "reset":
    case "declined":
    case "backup":
    case "backup_repeat":
    case "resumed":
      return true;
    // ⚠ A failure report. It self-erasing is the cycle-416 defect.
    case "backup_unsent":
    // Retired in cycle 427, but an older server may still say them, and both
    // describe a DEGRADED state with nothing else on screen to carry it.
    // Decided explicitly rather than inheriting a default.
    case "no_reading":
    case "capped":
      return false;
    // No line at all.
    case "ask_more_time":
    case "resolved":
      return false;
  }
}

/** The one-line reason the returned alarm shows when a window breaks.
 *  `null` = no line (an end that re-armed nothing, e.g. recovery). */
export function custodyHandBackLine(
  reason: string | null | undefined,
  memberName: string | null | undefined,
  valueText: string | null | undefined,
): string | null {
  const who = memberName && memberName.trim() ? memberName.trim() : "They";
  switch (reason) {
    case "worsening":
      // A QUIET window only, since cycle 427 (a declared one is asked).
      return valueText
        ? `${who} dropped to ${valueText} — resuming alerts`
        : `${who} dropped lower — resuming alerts`;
    case "expired_unimproved":
      return "Still low when the window ended — resuming alerts";
    case "caregiver_declined":
      // Cycle 427: a "no" (or 60 s of silence) to "Is <name> still
      // treating?" — a fresh search, calls first.
      return `Nobody still treating ${who} — calling the heroes`;
    case "expired_dark":
      // Cycle 427: the window ran out on a dark feed and the check-in
      // went unanswered — a fresh search, calls first.
      return "No reading and no answer when the window ended — calling the heroes";
    case "resumed":
      return "Alerts resumed";
    // Cycle 427 retired `stale` (a dark feed holds the window) and
    // `backup` (help keeps the window) — neither is an end any more.
    default:
      return null;
  }
}

/** Stable identity for ONE posed question — the speech edge is keyed on
 *  it so a redelivered frame (NOTIFY duplicates, reconnect replays) never
 *  re-speaks, while the `more_time` follow-up (a new question, a new
 *  deadline) does. */
export function checkinKey(
  alertId: string | null | undefined,
  question: CustodyCheckinQuestion | null | undefined,
  expiresAt: string | number | null | undefined,
): string | null {
  if (!alertId || !question || expiresAt == null || expiresAt === "") {
    return null;
  }
  const ms =
    typeof expiresAt === "number" ? expiresAt : Date.parse(expiresAt);
  if (!Number.isFinite(ms)) return null;
  return `${alertId}:${question}:${ms}`;
}

/** A question is on the screen and still answerable. FAILS CLOSED: a
 *  missing or unparseable deadline is NOT pending, so nothing can be
 *  answered — and therefore nothing extended — on a bad value. */
export function checkinPendingAt(
  nowMs: number,
  question: CustodyCheckinQuestion | null | undefined,
  expiresAt: string | number | null | undefined,
): boolean {
  if (!question || expiresAt == null || expiresAt === "") return false;
  const ms =
    typeof expiresAt === "number" ? expiresAt : Date.parse(expiresAt);
  if (!Number.isFinite(ms)) return false;
  return nowMs < ms;
}

/** The window the ring counts down from. Cycle 427: every window is the
 *  ONE 600 s duration — a tap, a re-tap, an extension, a reset — so the
 *  ring's total never depends on the count. (A prompt's 60 s hold at the
 *  end of a window is drawn by the same ring against the same total; the
 *  sheet's own countdown says the 60 s.) */
export function ringTotalMs(): number {
  return TREATING_RING_TOTAL_MS;
}

/** Ring fraction REMAINING, clamped to [0, 1]. Driven off the SERVER
 *  `custody_until` on every render (re-syncs on every heartbeat), never a
 *  client timer, so a drifted client self-corrects. */
export function ringFraction(
  remainingMs: number | null | undefined,
  totalMs: number = TREATING_RING_TOTAL_MS,
): number {
  if (remainingMs == null || !Number.isFinite(remainingMs) || totalMs <= 0) {
    return 0;
  }
  return Math.max(0, Math.min(1, remainingMs / totalMs));
}

/** Cycle 409 — the timer screen's header for either KIND of window.
 *
 *  A snooze shows the SAME ring and the same two loud actions as a
 *  declaration, but it must never be described as treating: nobody said
 *  so (cycle 393's "never speaks as a declaration"). The tag names the
 *  member; the line under it says what is actually known. */
export function custodyScreenCopy(
  kind: "declared" | "quiet" | null,
  memberName: string | null | undefined,
  contactName: string | null | undefined,
): { tag: string; who: string } {
  const name = memberName && memberName.trim() ? memberName.trim() : "";
  if (kind === "quiet") {
    return {
      tag: name ? `Snoozed · ${name}` : "Snoozed",
      who: "Alerts paused — nobody has said they're treating",
    };
  }
  return {
    tag: name ? `Treating · ${name}` : "Treating",
    who:
      contactName && contactName.trim()
        ? `${contactName.trim()} is treating`
        : "Someone is treating",
  };
}

/** ── Cycle 451: the prompt-render claim, once per PROMPT ────────────────
 *
 *  ⚠ THE CLAIM OUTLIVES THE SURFACE, ON PURPOSE. Cycle 447 kept it in a
 *  `useRef` inside `TreatingScreen`, which is the component's lifetime —
 *  correct until this cycle, because a bedside treating window had no
 *  chrome and therefore no way to leave the Now screen. With the rail and
 *  the tab bar back, a trip to History and back unmounts the screen, the
 *  ref resets, and the device claims the SAME prompt a second time. The
 *  episode report reads these rows to answer "was this prompt ever
 *  drawn?", so a duplicate is not cosmetic: it inflates the one signal
 *  that says a phone showed a question it never showed.
 *
 *  Module-level, so it survives a remount and a route change within the
 *  session. A full reload does re-claim — that is a new page, not a
 *  re-mount, and it is the same device saying it drew the prompt again,
 *  which is true.
 *
 *  ⚠ TELEMETRY ONLY. Nothing rendered, gated, sounded or written to the
 *  server depends on this; a failure to claim costs one ledger row.
 */
const PROMPT_CLAIM_CAP = 32;
const claimedPromptKeys: string[] = [];

/** The claim key. The ALERT id is in it so two episodes (or two members
 *  on one screen) cannot collide on a shared kind + deadline. */
export function promptClaimKey(
  alertId: string | null | undefined,
  question: string | null | undefined,
  deadline: string | null | undefined,
): string | null {
  if (!alertId || !question) return null;
  return `${alertId}|${question}|${deadline ?? ""}`;
}

/** True exactly once per key. Oldest-first eviction past the cap — a
 *  household does not hold 32 live prompts, so an eviction means the
 *  session has been open across many episodes and a re-claim there is
 *  harmless. */
export function claimPromptRender(key: string): boolean {
  if (claimedPromptKeys.includes(key)) return false;
  claimedPromptKeys.push(key);
  if (claimedPromptKeys.length > PROMPT_CLAIM_CAP) claimedPromptKeys.shift();
  return true;
}

/** Test-only reset. Never called by the app. */
export function resetPromptRenderClaims(): void {
  claimedPromptKeys.length = 0;
}

/** ── Cycle 451: the SPOKEN question, once per prompt ────────────────────
 *
 *  ⚠ SAME SHAPE, SAME REASON AS THE RENDER CLAIM ABOVE — and found by the
 *  same review. Cycle 407 kept the spoken key in a `useRef` on
 *  `TreatingScreen`, which is the component's lifetime. That was correct
 *  while a bedside treating window had no chrome to leave with; from this
 *  cycle a trip to History and back re-mounts the screen, the ref resets,
 *  and the kiosk SPEAKS THE SAME QUESTION AGAIN and arms a fresh
 *  re-prompt timer — once per round trip, unbounded.
 *
 *  Silence at a question is still answered: the ONE local re-prompt
 *  (`CHECKIN_REPROMPT_MS`) is untouched, and the server still resolves an
 *  unanswered prompt at its own deadline. What is removed is only the
 *  re-ask that a navigation caused. */
const spokenPromptKeys: string[] = [];

export function claimPromptSpoken(key: string): boolean {
  if (spokenPromptKeys.includes(key)) return false;
  spokenPromptKeys.push(key);
  if (spokenPromptKeys.length > PROMPT_CLAIM_CAP) spokenPromptKeys.shift();
  return true;
}

/** Test-only reset. Never called by the app. */
export function resetPromptSpokenClaims(): void {
  spokenPromptKeys.length = 0;
}
